import {
  AnyID,
  Getter,
  Item,
  LinkedItem,
  Partition,
  SceneObjects,
  Setter,
  Store,
  Types,
  Walls,
} from '@types'
import { shouldSkipGroupedDragForLinkedSon } from '@utils/atemporalSeatCushionLocalCoords'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import decomposeMatrix from '@utils/decomposeMatrix'
import isWall from '@utils/sceneObjects/assertions/isWall'
import { getWallCOBB } from '@utils/sceneObjects/walls/getWallCOBB'
import { Euler, Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

type RotationSnapshotItem = {
  position: [number, number, number]
  quaternion: [number, number, number, number]
}

function snapshotItemMatrix(matrix: Matrix4): RotationSnapshotItem {
  const { position, rotation } = decomposeMatrix(matrix)
  return {
    position: [position.x, position.y, position.z],
    quaternion: [rotation.x, rotation.y, rotation.z, rotation.w],
  }
}

function applyGroupRotationToSelected(
  store: Store,
  anchorId: AnyID,
  anchorMatrix: Matrix4,
  snapshot: { anchorId: string; items: Record<string, RotationSnapshotItem> },
) {
  const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
  const items = store.items
  const anchorStart = snapshot.items[anchorId]
  if (!anchorStart) return

  const { position: currentAnchorPos, rotation: currentAnchorRot } = decomposeMatrix(anchorMatrix)
  const anchorStartPos = toVector3(anchorStart.position)
  const anchorStartQuat = new Quaternion(...anchorStart.quaternion)
  const deltaQuat = currentAnchorRot.clone().multiply(anchorStartQuat.clone().invert())

  selectedItems.forEach((id) => {
    if (id === anchorId) return

    const item = items.get(id) as Item | Partition | LinkedItem
    if (!item) return
    if (shouldSkipGroupedDragForLinkedSon(item, selectedItems)) return

    const itemStart = snapshot.items[id]
    if (!itemStart) return

    const startPos = toVector3(itemStart.position)
    const startQuat = new Quaternion(...itemStart.quaternion)
    const offset = startPos.clone().sub(anchorStartPos)
    offset.applyQuaternion(deltaQuat)
    const newPosition = currentAnchorPos.clone().add(offset)
    const newRotation = deltaQuat.clone().multiply(startQuat)
    const newMatrix = new Matrix4().compose(newPosition, newRotation, toVector3([1, 1, 1]))

    item.matrix = newMatrix
    item.x = newPosition.x
    item.y = newPosition.y
    item.z = newPosition.z

    const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
    itemCenter.applyQuaternion(newRotation).add(newPosition)
    const rotationMatrix = new Matrix3().setFromMatrix4(
      new Matrix4().makeRotationFromQuaternion(newRotation),
    )

    store.items.set(id, item)
    store.sceneBoundingBoxes.set(id, {
      boundingBox: new OBB(
        itemCenter,
        toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
        rotationMatrix,
      ),
      type: 'items',
    })
  })
}

const setGlobalLastSelectedObjectId = (id: AnyID | '') => {
  if (typeof window === 'undefined') return
  if (!id) return
  ;(
    window as Window & {
      __ATEMPORAL_LAST_SELECTED__?: { id: string; ts: number }
    }
  ).__ATEMPORAL_LAST_SELECTED__ = {
    id: String(id),
    ts: Date.now(),
  }
}

const captureGlobalLastSelectedFromStore = (store: Store) => {
  const selectedFromLayer = Array.from(store.layers[store.currentLayer].selected.values())[0] as
    | AnyID
    | undefined
  const selectedId = (store.currentObjectID || selectedFromLayer || '') as AnyID | ''
  setGlobalLastSelectedObjectId(selectedId)
}

export type SceneActions = ReturnType<typeof createSceneActions>

export const createSceneActions = (set: Setter, get: Getter) => ({
  select: (id: AnyID, isCtrlPressed: boolean) => {
    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<string>
      if (
        (!isCtrlPressed && selectedItems.size < 2) ||
        (!isCtrlPressed && !selectedItems.has(id))
      ) {
        selectedItems.clear()
      }
      if (isCtrlPressed && selectedItems.has(id)) {
        selectedItems.delete(id)
        setGlobalLastSelectedObjectId(id)
        store.currentObjectID = ''
      } else {
        selectedItems.add(id)
        store.currentObjectID = id
        setGlobalLastSelectedObjectId(id)
      }
    })
  },
  unselect: (id: AnyID) => {
    set((store) => {
      store.layers[store.currentLayer].selected.delete(id)
      if (store.currentObjectID === id) {
        setGlobalLastSelectedObjectId(id)
        store.currentObjectID = ''
      }
    })
  },

  unselectAll: () => {
    set((store) => {
      captureGlobalLastSelectedFromStore(store)
      store.layers[store.currentLayer].selected.clear()
      store.layers[store.currentLayer].rotationSnapshot = null
      store.currentObjectID = ''
    })
  },
  // TODO: esto debería no existir: Es parte de multiselección
  deleteSelectedObjects: () => {
    const storeBeforeDelete = get()
    const wasSelected =
      storeBeforeDelete.currentObjectID &&
      storeBeforeDelete.layers[storeBeforeDelete.currentLayer].selected.has(
        storeBeforeDelete.currentObjectID,
      )

    get().recordDestructiveChange(() => {
    set((store: Store) => {
      const selected = store.layers[store.currentLayer].selected as Set<string>
      const removedIds = new Set<string>()

      const clearSnappedSidesReferences = () => {
        store.items.forEach((sceneItem) => {
          if (!sceneItem || typeof sceneItem !== 'object') return
          const sceneItemWithSnaps = sceneItem as unknown as {
            snappedSides?: Partial<Record<'left' | 'right', string>>
          }
          const snappedSides = sceneItemWithSnaps.snappedSides
          if (!snappedSides) return

          const left = snappedSides.left
          const right = snappedSides.right
          const shouldClearLeft =
            !!left && (removedIds.has(String(left)) || !store.items.has(String(left) as AnyID))
          const shouldClearRight =
            !!right && (removedIds.has(String(right)) || !store.items.has(String(right) as AnyID))

          if (!shouldClearLeft && !shouldClearRight) return

          sceneItemWithSnaps.snappedSides = {
            ...snappedSides,
            left: shouldClearLeft ? undefined : left,
            right: shouldClearRight ? undefined : right,
          }
        })
      }

      if (selected) {
        selected.forEach((el: string) => {
          if (!el) return

          const currItem = store.items.get(el) as SceneObjects
          if (!currItem) return
          removedIds.add(String(el))

          if (currItem.type === 'linkedItem') {
            const childs = currItem.childs as Set<string>
            childs.forEach((childID) => {
              const childItem = store.items.get(childID)
              removedIds.add(String(childID))
              if (childItem) {
                store.price -= childItem.price || 0
                store.items.delete(childID)
                store.sceneBoundingBoxes.delete(childID)
                store.collisionableItems.delete(childID)
                store.layers[store.currentLayer].items.delete(childID)
                store.layers[store.currentLayer].selected.delete(childID)
              }
            })
          }

          if (currItem.snappedWall) {
            const wall = store.items.get(currItem.snappedWall) as Walls
            if (wall) {
              if (currItem.tipoNormalizado === 'MAL') {
                wall.highItems.delete(el)
              }
              if (currItem.tipoNormalizado === 'MBJ' || currItem.tipoNormalizado === 'COL') {
                wall.lowItems.delete(el)
              }
            }
          }

          store.price -= currItem.price || 0

          store.items.delete(el)
          store.sceneBoundingBoxes.delete(el)
          store.collisionableItems.delete(el)
          store.sceneBoundingBoxes.delete(getWallCOBB(el))
          store.collisionableItems.delete(getWallCOBB(el))
          store.layers[store.currentLayer].items.delete(el)
        })

        clearSnappedSidesReferences()
        selected.clear()
      }

      store.currentObjectID = ''
    })
    })

    // Después de borrar, si había un item seleccionado, seleccionar automáticamente otro item universal
    if (wasSelected) {
      const storeAfterDelete = get()
      // Buscar el primer item universal disponible en la capa actual
      const availableItems = Array.from(
        storeAfterDelete.layers[storeAfterDelete.currentLayer].items.values(),
      )
      const universalItem = availableItems.find((itemId) => {
        const item = storeAfterDelete.items.get(itemId)
        return item && item.type === 'items' && (item as any).universal === true
      })

      if (universalItem) {
        // Usar la función select para asegurar que la selección se maneje correctamente
        get().select(universalItem, false)
      }
    }
  },

  setCurrentObjectID: (id: AnyID | '') => {
    set((store) => {
      if (!id && id !== '') return

      store.currentObjectID = id
      setGlobalLastSelectedObjectId(id)
    })
  },
  //TO-DO:esto no se esta usando no hace falta pa nada
  addBoundingBox: (id: string, boundingObject: { type: Types; boundingBox: OBB }) => {
    set((store) => {
      store.sceneBoundingBoxes.set(id, boundingObject)
    })
  },
  setDeltas: (
    deltas: Map<
      string,
      Array<{ targetId: string; deltaX: number; deltaY: number; deltaZ: number }>
    >,
  ) => {
    set((store) => {
      deltas.forEach((value, key) => {
        store.layers[store.currentLayer].deltas.set(key, value)
      })
    })
  },
  captureMultiSelectRotationSnapshot: (anchorId: AnyID) => {
    set((store) => {
      const layer = store.layers[store.currentLayer]
      const selectedItems = layer.selected as Set<AnyID>
      if (selectedItems.size < 2) {
        layer.rotationSnapshot = null
        return
      }

      const snapshotItems: Record<string, RotationSnapshotItem> = {}
      selectedItems.forEach((id) => {
        const item = store.items.get(id)
        if (!item?.matrix) return
        if (shouldSkipGroupedDragForLinkedSon(item, selectedItems)) return
        snapshotItems[id] = snapshotItemMatrix(item.matrix as Matrix4)
      })

      const anchorItem = store.items.get(anchorId)
      if (anchorItem?.matrix && !snapshotItems[anchorId]) {
        snapshotItems[anchorId] = snapshotItemMatrix(anchorItem.matrix as Matrix4)
      }

      layer.rotationSnapshot =
        Object.keys(snapshotItems).length > 0 ? { anchorId, items: snapshotItems } : null
    })
  },
  clearMultiSelectRotationSnapshot: () => {
    set((store) => {
      store.layers[store.currentLayer].rotationSnapshot = null
    })
  },
  notSameItems: () => {
    const store = get()
    const selectedItems = new Set(
      Array.from(store.layers[store.currentLayer].selected).filter((id) => store.items.has(id)),
    )

    if (selectedItems.size < 2) return false

    let firstIdUnidades: string | null = null

    for (const el of selectedItems) {
      const currItem = store.items.get(el) as SceneObjects

      if (!currItem) {
        console.warn(`⚠️ El objeto con ID ${el} no se encontró en store.items`)
        continue
      }

      if (!currItem.idUnidades) {
        console.warn(`⚠️ El objeto con ID ${el} no tiene la propiedad idUnidades`, currItem)
        continue
      }

      if (!firstIdUnidades) {
        firstIdUnidades = currItem.idUnidades
      } else if (firstIdUnidades !== currItem.idUnidades) {
        return true
      }
    }

    return false
  },

  updateSelectedObjectsPosition: (currentObjectId: AnyID, matrix: Matrix4) => {
    // LOG: Ver si se está llamando updateSelectedObjectsPosition
    const itemBefore = get().items.get(currentObjectId)
    if (itemBefore && itemBefore.matrix) {
      const posBefore = new Vector3()
      itemBefore.matrix.decompose(posBefore, new Quaternion(), new Vector3())
      const posNew = new Vector3()
      matrix.decompose(posNew, new Quaternion(), new Vector3())
      // console.log('🔄 [updateSelectedObjectsPosition] Moviendo item:', {
      //   itemID: currentObjectId,
      //   from: { x: posBefore.x, y: posBefore.y, z: posBefore.z },
      //   to: { x: posNew.x, y: posNew.y, z: posNew.z }
      // })
    }

    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
      const items = store.items
      const deltas = store.layers[store.currentLayer].deltas

      if (selectedItems.size < 2) return

      // Obtener la posición del elemento que se está arrastrando
      const currentItem = items.get(currentObjectId) as Item | Partition | LinkedItem
      if (!currentItem) return

      const { position: currentPosition } = decomposeMatrix(matrix)

      // Aplicar restricción al currentPosition.y también para evitar que se propague valores negativos
      const mode = store.mode
      let restrictedCurrentY = currentPosition.y
      if (mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS') {
        // Restringir mínimo a 0 siempre
        restrictedCurrentY = Math.max(0, restrictedCurrentY)

        // Si el item actual tiene altura, aplicar restricción máxima
        if ('height' in currentItem && currentItem.height) {
          // Obtener altura máxima de la pared
          let maxWallHeight: number | null = null

          // Si el item está pegado a una pared, usar esa pared
          if ('snappedWall' in currentItem && currentItem.snappedWall) {
            const wall = items.get(currentItem.snappedWall) as Walls
            if (isWall(wall)) {
              maxWallHeight = wall.height
            }
          } else {
            // Si no está pegado, obtener la altura máxima de todas las paredes
            let maxHeight = 0
            items.forEach((sceneObject) => {
              if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                maxHeight = sceneObject.height
              }
            })
            maxWallHeight = maxHeight > 0 ? maxHeight : null
          }

          // Restringir máximo considerando la altura del mueble
          if (maxWallHeight !== null) {
            const maxYAllowed = maxWallHeight - currentItem.height
            restrictedCurrentY = Math.min(restrictedCurrentY, maxYAllowed)
          }
        }
      }

      // Actualizar las posiciones de todos los elementos seleccionados
      selectedItems.forEach((id) => {
        if (id === currentObjectId) return // Saltar el elemento que se está arrastrando

        const item = items.get(id) as Item | Partition | LinkedItem
        if (!item) return
        if (shouldSkipGroupedDragForLinkedSon(item, selectedItems)) return

        const itemDeltas = deltas?.get(currentObjectId)
        if (!itemDeltas) return

        itemDeltas.forEach((delta) => {
          if (delta.targetId === id) {
            // Usar el currentPosition.y restringido como base
            let newY = restrictedCurrentY + delta.deltaY

            // Aplicar restricción de altura en modo 3D o alzados
            if (
              mode === 'MODE_3D_VIEW' ||
              mode === 'MODE_FORCE_ALZADOS' ||
              mode === 'MODE_ALZADOS'
            ) {
              // Restringir mínimo a 0 siempre
              newY = Math.max(0, newY)

              // Si el item tiene altura, aplicar restricción máxima
              if ('height' in item && item.height) {
                // Obtener altura máxima de la pared
                let maxWallHeight: number | null = null

                // Si el item está pegado a una pared, usar esa pared
                if ('snappedWall' in item && item.snappedWall) {
                  const wall = items.get(item.snappedWall) as Walls
                  if (isWall(wall)) {
                    maxWallHeight = wall.height
                  }
                } else {
                  // Si no está pegado, obtener la altura máxima de todas las paredes
                  let maxHeight = 0
                  items.forEach((sceneObject) => {
                    if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                      maxHeight = sceneObject.height
                    }
                  })
                  maxWallHeight = maxHeight > 0 ? maxHeight : null
                }

                // Restringir máximo considerando la altura del mueble
                if (maxWallHeight !== null) {
                  const maxYAllowed = maxWallHeight - item.height
                  newY = Math.min(newY, maxYAllowed)
                }
              }
            }

            const newPosition = toVector3([
              currentPosition.x + delta.deltaX,
              newY,
              currentPosition.z + delta.deltaZ,
            ])

            const { rotation: originalQuaternion } = decomposeMatrix(item.matrix)
            const newMatrix = new Matrix4()
              .makeRotationFromQuaternion(originalQuaternion)
              .setPosition(newPosition)

            // CORRECCIÓN FINAL: asegurar que newY nunca sea negativo (última línea de defensa)
            const finalY = Math.max(0, newY)

            // Actualizar el item directamente
            item.matrix = newMatrix
            item.x = newPosition.x
            item.y = finalY
            item.z = newPosition.z

            // Si se corrigió el Y, actualizar la matriz también
            if (finalY !== newY) {
              newMatrix.setPosition(newPosition.x, finalY, newPosition.z)
              item.matrix = newMatrix
            }

            // Actualizar en el store
            store.items.set(id, item)

            // Actualizar también el boundingBox para que se refleje correctamente
            const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
            const centerPosition = toVector3()
            newMatrix.decompose(centerPosition, originalQuaternion, toVector3())
            itemCenter.applyQuaternion(originalQuaternion).add(centerPosition)

            const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(originalQuaternion)
            const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

            store.sceneBoundingBoxes.set(id, {
              boundingBox: new OBB(
                itemCenter,
                toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
                rotationMatrix,
              ),
              type: 'items',
            })
          }
        })
      })
    })
  },
  updateSelectedObjectsTraslation: (currentObjectId: AnyID, matrix: Matrix4, _prevRot?: number) => {
    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
      if (selectedItems.size < 2) return

      const snapshot = store.layers[store.currentLayer].rotationSnapshot
      if (!snapshot || snapshot.anchorId !== currentObjectId) return

      applyGroupRotationToSelected(store, currentObjectId, matrix, snapshot)
    })
  },
  updateSelectedObjectsTraslation2D: (
    currentObjectId: AnyID,
    matrix: Matrix4,
    _prevRot?: number,
  ) => {
    set((store) => {
      const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
      if (selectedItems.size < 2) return

      const snapshot = store.layers[store.currentLayer].rotationSnapshot
      if (!snapshot || snapshot.anchorId !== currentObjectId) return

      applyGroupRotationToSelected(store, currentObjectId, matrix, snapshot)
    })
  },
  projectItemsOnWall: (itemID: AnyID, wallID: AnyID, matrix: Matrix4, prevRot: number) => {
    set((store) => {
      const items = store.items
      const selectedItems = store.layers[store.currentLayer].selected as Set<string>
      const deltas = store.layers[store.currentLayer].deltas as Map<
        string,
        { deltaX: number; deltaY: number; deltaZ: number }[]
      >
      const item = items.get(itemID) as Item
      const wall = items.get(wallID) as Walls

      if (!item || !wall) return

      // Proyectar el item principal en la pared
      const itemMorphs = item.morphs.find((el) => el.name === 'Ancho')
      const itemWidth = itemMorphs?._default ?? itemMorphs?.default ?? 0

      const { rotation, width } = wall

      const itemPosition = toVector3()
      matrix.decompose(itemPosition, toQuaternion(), toVector3())
      const end = toVector3(wall.end)
      const start = toVector3(wall.start)
      const wallDirection = end.clone().sub(start)
      const itemToStart = itemPosition.clone().sub(start)
      const projectedVector = itemToStart.projectOnVector(wallDirection.clone().normalize())

      if (Math.abs(projectedVector.x) === 0) {
        projectedVector.x = 0
      }
      if (Math.abs(projectedVector.z) === 0) {
        projectedVector.z = 0
      }
      if (Math.abs(projectedVector.x) > width - itemWidth) {
        if (wallDirection.x < 0) {
          projectedVector.x = itemWidth - width
        } else {
          projectedVector.x = -itemWidth + width
        }
      }
      if (Math.abs(projectedVector.x) < 0) {
        projectedVector.x = 0
      }
      if (Math.abs(projectedVector.z) > width - itemWidth) {
        if (wallDirection.z < 0) {
          projectedVector.z = itemWidth - width
        } else {
          projectedVector.z = -itemWidth + width
        }
      }
      if (Math.abs(projectedVector.z) < 0) {
        projectedVector.z = -itemWidth
      }

      let length = projectedVector.length()
      const wallLength = wallDirection.length()
      let alongWall = projectedVector.length()

      if (projectedVector.dot(wallDirection.normalize()) < 0) {
        alongWall = 0
      } else if (alongWall > wallLength) {
        alongWall = wallLength - itemWidth
      }

      const adjustedPosition = start
        .clone()
        .add(wallDirection.normalize().multiplyScalar(alongWall))

      if (length < 0) {
        length = 0
      }
      if (length > width || length + item.width > width) {
        length = width
      }

      if (!item) return

      // Aplicar restricción de altura en modo 3D o alzados
      let restrictedY = itemPosition.y
      const mode = store.mode
      if (mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS') {
        // Restringir mínimo a 0 siempre
        restrictedY = Math.max(0, restrictedY)

        // Si el item tiene altura, aplicar restricción máxima
        if ('height' in item && item.height) {
          // Usar la altura de la pared a la que está pegado
          const maxWallHeight = wall.height

          // Restringir máximo considerando la altura del mueble
          const maxYAllowed = maxWallHeight - item.height
          restrictedY = Math.min(restrictedY, maxYAllowed)
        }
      }

      item.matrix.makeRotationFromEuler(new Euler(0, -rotation, 0, 'XYZ'))

      if (!item.visible) item.visible = true
      item.matrix.setPosition(adjustedPosition.x, restrictedY, adjustedPosition.z)
      item.x = adjustedPosition.x
      item.z = adjustedPosition.z
      item.y = restrictedY
      item.snappedWall = wallID
      if (item.canCollide !== false && item.tipoNormalizado === 'MAL') {
        wall.highItems.add(itemID)
      }
      if (
        item.canCollide !== false &&
        (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL')
      ) {
        wall.lowItems.add(itemID)
      }
      items.set(itemID, item)
      selectedItems.forEach((id) => {
        if (id === itemID) return // Saltar el elemento que se está rotando
        const selectedItem = items.get(id) as Item
        if (!selectedItem) return
        const itemDeltas = deltas.get(itemID)
        if (!itemDeltas) return
        const delta = itemDeltas.find(
          (d) =>
            d.deltaX === selectedItem.x - item.x &&
            d.deltaY === selectedItem.y - item.y &&
            d.deltaZ === selectedItem.z - item.z,
        )
        if (!delta) return
        const deltaVector = toVector3([delta.deltaX, delta.deltaY, delta.deltaZ])
        // Proyectar el delta en la dirección del projectedVector
        const deltaProjected = deltaVector.projectOnVector(projectedVector)
        // Calcular la nueva posición paralela al projectedVector
        const newPosition = adjustedPosition.clone().add(deltaProjected)

        selectedItem.matrix.setPosition(newPosition.x, newPosition.y, newPosition.z)
        selectedItem.x = newPosition.x
        selectedItem.y = newPosition.y
        selectedItem.z = newPosition.z

        items.set(id, selectedItem)
      })
    })
  },
  getFavorites: () => {
    const store = get()
    const selectedItems = store.layers[store.currentLayer].selected as Set<AnyID>
    const items = store.items
    const deltas = store.layers[store.currentLayer].deltas as Map<
      AnyID,
      { deltaX: number; deltaY: number; deltaZ: number }[]
    >

    const favorites = {
      items: new Map() as Map<AnyID, Item | LinkedItem>,
      deltas: new Map<AnyID, { deltaX: number; deltaY: number; deltaZ: number }[]>(),
    }

    selectedItems.forEach((id) => {
      const item = items.get(id) as Item | LinkedItem
      if (item) {
        favorites.items.set(id, item)
      }
    })

    if (selectedItems.size > 1) {
      favorites.deltas = deltas
    }

    return favorites
  },
})
